import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  UserPlus,
  Edit,
  Trash2,
  MoreHorizontal,
  Shield,
  Eye,
  PenTool,
  Settings,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';


const API = "/api";

const UserManagement = ({ users, onRefresh, loading, roleConfig, statusConfig, setActiveTab }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { toast } = useToast();

  const handlePermissionChange = async (userId, permission, value) => {
    try {
      await axios.put(`${API}/users/${userId}`, { [permission]: value });
      onRefresh();
      toast({
        title: "Success",
        description: `Permission ${value ? 'granted' : 'revoked'} successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update permission",
        variant: "destructive",
      });
    }
  };

  
  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        onRefresh();
        toast({
          title: "Success",
          description: "User deleted successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to delete user",
          variant: "destructive",
        });
      }
    }
  };

  const handleEditUser = (user) => {
    // Prevent opening the same user multiple times
    if (selectedUser?.id === user.id && showEditModal) {
      return;
    }
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleStatusChange = async (userId, newStatus, username) => {
    const statusText = newStatus === 'active' ? 'activate' : 'deactivate';
    const confirmMessage = `Are you sure you want to ${statusText} user "${username}"?`;

    if (window.confirm(confirmMessage)) {
      try {
        await axios.put(`${API}/users/${userId}`, { status: newStatus });
        onRefresh();
        toast({
          title: "Success",
          description: `User ${statusText}d successfully`,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error.response?.data?.detail || `Failed to ${statusText} user`,
          variant: "destructive",
        });
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleIcon = (role) => {
    const config = roleConfig[role];
    if (!config) return Shield;
    return config.icon;
  };

  const getPermissionDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Full control including admin tasks';
      case 'contributor':
        return 'Can contribute content but not edit or delete existing content';
      case 'read_only':
        return 'Read only access';
      default:
        return 'Unknown permission level';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">User Management</h3>
          <p className="text-sm text-slate-600">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Permission Levels Info */}
      <Card className="bg-slate-50 border-slate-200">
        <CardHeader>
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center">
            <Shield className="w-4 h-4 mr-2" />
            Permission Levels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <Shield className="w-5 h-5 text-red-600" />
              <div>
                <div className="font-medium text-red-800">Full Control</div>
                <div className="text-xs text-red-600">Admin tasks, manage users</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <PenTool className="w-5 h-5 text-blue-600" />
              <div>
                <div className="font-medium text-blue-800">Contributor</div>
                <div className="text-xs text-blue-600">Create content only</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <Eye className="w-5 h-5 text-gray-600" />
              <div>
                <div className="font-medium text-gray-800">Read Only</div>
                <div className="text-xs text-gray-600">View access only</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Departments</TableHead>
              <TableHead>Product Permissions</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              const roleConfigData = roleConfig[user.role] || roleConfig.read_only;
              const statusConfigData = statusConfig[user.status] || statusConfig.pending;
              const StatusIcon = statusConfigData.icon;

              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium text-slate-900">{user.full_name}</div>
                      <div className="text-sm text-slate-500">
                        @{user.username} • {user.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${roleConfigData.color} border flex items-center w-fit`}>
                      <RoleIcon className="w-3 h-3 mr-1" />
                      {roleConfigData.label}
                    </Badge>
                    <div className="text-xs text-slate-500 mt-1">
                      {getPermissionDescription(user.role)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusConfigData.color} border flex items-center w-fit`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfigData.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.departments.length > 0 ? (
                        user.departments.map((dept) => (
                          <Badge key={dept} variant="outline" className="text-xs">
                            {dept.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-xs text-slate-500">No departments</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.can_add_products || false}
                          onChange={(e) => handlePermissionChange(user.id, 'can_add_products', e.target.checked)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs">Add Products</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={user.can_delete_products || false}
                          onChange={(e) => handlePermissionChange(user.id, 'can_delete_products', e.target.checked)}
                          className="rounded border-slate-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-xs">Delete Products</span>
                      </label>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                          <Settings className="mr-2 h-4 w-4" />
                          Settings
                        </DropdownMenuItem>
                        {user.status === 'active' ? (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange(user.id, 'inactive', user.username)}
                            className="text-orange-600"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange(user.id, 'active', user.username)}
                            className="text-green-600"
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {users.length === 0 && (
          <div className="text-center py-12">
            <div className="text-slate-500 mb-2">No users found</div>
            <Button
              onClick={() => setShowAddModal(true)}
              variant="outline"
              className="text-slate-600"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add your first user
            </Button>
          </div>
        )}
      </Card>

      {/* Modals */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          onRefresh();
        }}
      />

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          // Delay cleanup to prevent state conflicts
          setTimeout(() => setSelectedUser(null), 100);
        }}
        user={selectedUser}
        onSuccess={() => {
          setShowEditModal(false);
          // Delay cleanup and refresh to prevent state conflicts
          setTimeout(() => {
            setSelectedUser(null);
            onRefresh();
          }, 100);
        }}
      />
    </div>
  );
};

export default UserManagement;